import {
    Component,
    DestroyRef,
    OnDestroy,
    OnInit,
    Signal,
    computed,
    effect,
    inject,
    input,
    ChangeDetectionStrategy
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
    DialogService,
    ShButtonComponent,
    ShSnackbarService,
    ShSpinnerComponent,
    ShStepperComponent,
    ShStepperItemComponent
} from '@gbr-monorepo/gbr-shui';
import { Season } from '@gbr-monorepo/interfaces';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { Store, select } from '@ngxs/store';
import { EMPTY, catchError, filter, tap } from 'rxjs';
import { SnackbarConfig } from '../../../../shared/configs/snackbar.config';
import { CancleSeasonCreationDialogComponent } from '../../../dialogs/cancel-application/cancel-application.component';
import { SeasonFormStepper } from '../../../models/season-create.model';
import { SeasonFormGroup } from '../../../models/season-form.model';
import { SeasonCreateOperations } from '../../../operations/season-creation.operations';
import { SeasonService } from '../../../services/season.service';
import {
    DecrementStepper,
    IncrementStepper,
    ResetSeasonForm,
    ResetStepper,
    SetStepper,
    SubmitForm
} from '../../../state/season-form/season-form.action';
import { SeasonFormState } from '../../../state/season-form/season-form.state';
import { SeasonState } from '../../../state/season/season.state';
import { BudgetComponent } from '../season-creation-budget/budget.component';
import { SalaryWeightComponent } from '../season-creation-salary-weight/salary-weight.component';
import { SeasonInfoComponent } from '../season-creation-season-info/season-info.component';
import { SocialCriteriaComponent } from '../season-creation-social-criteria/social-criteria.component';
import { SummaryComponent } from '../season-creation-summary/summary.component';
import { TimeframeComponent } from '../season-creation-timeframe/timeframe.component';
import { CREATE_SEASON_STEPS } from './config';
@Component({
    selector: 'app-season-form',
    host: { class: 'block h-full' },
    imports: [
        ShStepperComponent,
        ShStepperItemComponent,
        SeasonInfoComponent,
        TimeframeComponent,
        BudgetComponent,
        SocialCriteriaComponent,
        SalaryWeightComponent,
        SummaryComponent,
        NgxsFormDirective,
        ReactiveFormsModule,
        ShSpinnerComponent,
        TranslateModule,
        ShButtonComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './season-form.component.html'
})
export class SeasonFormComponent implements OnInit, OnDestroy {
    public mode = input.required<'create' | 'edit'>();

    private store = inject(Store);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private translate = inject(TranslateService);
    private dialog = inject(DialogService);
    private snackbar = inject(ShSnackbarService);
    private seasonService = inject(SeasonService);

    protected seasonStepper: Signal<SeasonFormStepper> = select(SeasonFormState.getFormStepper);
    protected season: Signal<Season> = select(SeasonState.getSeason);
    protected isSavingSeason: Signal<boolean> = select(SeasonFormState.isSavingSeason);

    protected currentStepIndex: Signal<number> = computed(() => this.seasonStepper().selected);
    protected isEditMode: Signal<boolean> = computed(() => this.mode() === 'edit');
    protected disabledSteps: Signal<number[]> = computed(() =>
        this.isEditMode()
            ? []
            : CREATE_SEASON_STEPS.map((_, index) => index).filter(index => this.seasonStepper().active < index)
    );

    public steps = this.translatedSteps();
    public seasonCreationForm: FormGroup;

    public get seasonInfo(): SeasonFormGroup['seasonInfo'] {
        return this.seasonCreationForm?.get('seasonInfo') as SeasonFormGroup['seasonInfo'];
    }

    public get timeframe(): SeasonFormGroup['timeframe'] {
        return this.seasonCreationForm?.get('timeframe') as SeasonFormGroup['timeframe'];
    }

    public get budget(): SeasonFormGroup['budget'] {
        return this.seasonCreationForm?.get('budget') as SeasonFormGroup['budget'];
    }

    public get socialCriteria(): SeasonFormGroup['socialCriteria'] {
        return this.seasonCreationForm?.get('socialCriteria') as SeasonFormGroup['socialCriteria'];
    }

    public get salaryWeight(): SeasonFormGroup['salary'] {
        return this.seasonCreationForm?.get('salary') as SeasonFormGroup['salary'];
    }

    public get isFromValidInCorrectStep(): boolean {
        switch (this.currentStepIndex()) {
            case 0:
                return this.seasonInfo.valid;
            case 1:
                return this.timeframe.valid;
            case 2:
                return this.budget.valid;
            case 3:
                return this.socialCriteria.valid;
            case 4:
                return this.salaryWeight.valid;
            default:
                return true;
        }
    }

    constructor() {
        effect(() => {
            const season = this.season();

            if (this.isEditMode() && season) {
                this.initializeForm(season);
            } else if (!this.isEditMode()) {
                this.initializeForm();
            }
        });

        effect(() => {
            this.disabledSteps();
            this.seasonStepper();
            setTimeout(() => this.syncStepperItems());
        });
    }

    public ngOnInit(): void {
        this.listenToLanguageChange();
    }

    public ngOnDestroy(): void {
        this.store.dispatch(new ResetStepper());
        this.store.dispatch(new ResetSeasonForm());
    }

    public onCancel(): void {
        this.dialog
            .open(CancleSeasonCreationDialogComponent, {})
            .afterClosed.pipe(
                filter(result => result === 'cancel'),
                tap(() => {
                    this.router.navigate(['/']);
                    this.store.dispatch(new ResetStepper());
                    this.store.dispatch(new ResetSeasonForm());
                })
            )
            .subscribe();
    }

    public onNext(): void {
        this.store.dispatch(new IncrementStepper());
    }

    public onBack(): void {
        this.store.dispatch(new DecrementStepper());
    }

    public onSubmit(): void {
        this.store
            .dispatch(new SubmitForm())
            .pipe(
                tap(() => {
                    this.router.navigate(['/season/success']);
                    this.store.dispatch(new ResetStepper());
                    this.store.dispatch(new ResetSeasonForm());
                }),
                catchError(error => {
                    console.log('ERROR', error);
                    switch (error.error.message) {
                        case 'Specified start and end date cannot cross the ending of a season.':
                            this.snackbar.open({
                                type: 'error',
                                description: this.translate.instant(
                                    'seasons.season-creation.errors.invalid-timeframe.description'
                                ),
                                title: this.translate.instant('seasons.season-creation.errors.invalid-timeframe.title'),
                                duration: SnackbarConfig.duration,
                                position: 'bottom-center'
                            });
                            break;
                        default:
                            this.snackbar.open({
                                type: 'error',
                                description: this.translate.instant(
                                    'seasons.season-creation.errors.general.description'
                                ),
                                title: this.translate.instant('seasons.season-creation.errors.general.title'),
                                duration: SnackbarConfig.duration,
                                position: 'bottom-center'
                            });
                            break;
                    }

                    return EMPTY;
                })
            )
            .subscribe();
    }

    public handleNavigateToStep(step: number): void {
        const stepValidity = Array.from({ length: CREATE_SEASON_STEPS.length }, (_, i) => i).map(
            i => !this.isStepInvalid(i)
        );

        let highestInvalidStep = stepValidity.findIndex(valid => !valid);
        highestInvalidStep = highestInvalidStep === -1 ? CREATE_SEASON_STEPS.length : highestInvalidStep;

        if (step <= highestInvalidStep) {
            this.store.dispatch(new SetStepper(step));
        }
    }

    public handleStepperValueChange(event: Event): void {
        const value = Number((event.currentTarget as HTMLElement & { value: number | string }).value);

        if (Number.isInteger(value)) {
            this.handleNavigateToStep(value - 1);
        }
    }

    private isStepInvalid(step: number): boolean {
        switch (step) {
            case 0:
                return this.seasonInfo.invalid;
            case 1:
                return this.timeframe.invalid;
            case 2:
                return this.budget.invalid;
            case 3:
                return this.socialCriteria.invalid;
            case 4:
                return this.salaryWeight.invalid;
            default:
                return false;
        }
    }

    private initializeForm(season?: Season): void {
        this.seasonCreationForm = SeasonCreateOperations.initializeForm(season, this.seasonService);
        this.seasonCreationForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    private syncStepperItems(): void {
        const stepperItems = document.querySelectorAll<HTMLElement>('app-season-form sh-stepper-item');

        stepperItems.forEach((item, index) => {
            (item as HTMLElement & { disabled: boolean }).disabled = this.disabledSteps().includes(index);
        });
    }

    private listenToLanguageChange(): void {
        this.translate.onLangChange
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => (this.steps = this.translatedSteps()))
            )
            .subscribe();
    }

    private translatedSteps(): typeof CREATE_SEASON_STEPS {
        return CREATE_SEASON_STEPS.map(step => ({
            ...step,
            label: this.translate.instant(step.label)
        }));
    }
}
